import { useCallback, useEffect, useState } from 'react';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { HouseholdStatus, PaymentProcessor, StripeSubscriptionLevel } from '../../../../common/src/model.ts';
import { OnboardingPages } from '../../../../common/src/pages.ts';
import { LocalStorageKey, localStorageSet } from '../../data/client_local_storage.ts';
import { useHousehold } from '../../data/use_household.tsx';
import {
  AppleProductId,
  AppleSubscriptionInfo,
  isInsideAppleAppStoreApp,
  isInsideGooglePlayApp,
  parseAppleDateAsTimestamp,
  pollForAppleSubscriptionInfo,
} from '../../util/billing.ts';
import { Button } from './button.tsx';
import { getRedeemablePromoCampaignSlug, goToNextOnboardingStep, Message, startStripeCheckout } from './common.tsx';
import { Frame } from './frame.tsx';
import { SubscribePage } from './onboarding_flow.ts';

// Copy for promotional campaign landing pages (GitHub issue #3551), keyed by the campaign slug the
// server knows (server/src/billing/promo_campaigns.ts). The server applies the Stripe promotion; the
// client only changes what the Subscribe page says.
const promoCampaignCopy: { [slug: string]: { subhead: string } } = {
  'family-worship': { subhead: 'A gift with your Family Worship hymnal: full access for your entire household.' },
};

export const SubscribePayment = ({
  onboardingPage = OnboardingPages.SubscribePayment,
}: {
  onboardingPage?: SubscribePage;
}) => {
  const { household } = useHousehold();
  const navigate = useNavigate();
  // Redeemable in a browser only, not in the store apps (GitHub issue #3551).
  // Read once when the page opens: the page stays mounted while it animates out, and re-reading the
  // next page's URL there (no `promo`) would make it act as the plain subscribe page and redirect.
  const [promo] = useState(() => getRedeemablePromoCampaignSlug());

  // Old iOS app builds push subscription info via globalThis.receiveSubscriptionInfo when
  // a purchase completes (see Billing.swift's purchaseProduct flow). New iOS builds (4.17+)
  // can't push under iOS 18 with limitsNavigationsToAppBoundDomains, so the webapp polls
  // requestSubscriptionInfo from onSubscribeApple instead. Keep this persistent listener
  // for backward compatibility with old iOS apps still in the wild.
  // TODO(remove after 2027-01-01): delete this entire effect once no old iOS apps remain;
  // pollForAppleSubscriptionInfo in onSubscribeApple covers the new iOS path.
  useEffect(() => {
    if (!isInsideAppleAppStoreApp()) return;
    const previous = globalThis.receiveSubscriptionInfo;
    globalThis.receiveSubscriptionInfo = (info: AppleSubscriptionInfo) => {
      storeAppleSubscription(info);
      void goToNextOnboardingStep(navigate, { from: onboardingPage, action: 'purchasedInApp' });
    };
    return () => {
      globalThis.receiveSubscriptionInfo = previous;
    };
  }, [navigate, onboardingPage]);

  const onAnnually = useCallback(() => {
    if (!household?.token) {
      // a promotional campaign's visitor reads the offer first; claiming it starts sign-in or household
      // creation, which comes back to checkout with the promo still on the URL (GitHub issue #3551)
      if (promo) {
        void goToNextOnboardingStep(navigate, { from: onboardingPage, action: 'noHousehold' });
      }
      return;
    }
    if (isInsideAppleAppStoreApp()) {
      void onSubscribeApple(household.token, AppleProductId.Annual, navigate, onboardingPage);
    } else {
      void startStripeCheckout(household.token, StripeSubscriptionLevel.Annual, promo);
    }
  }, [household, navigate, onboardingPage, promo]);

  const onMonthly = useCallback(() => {
    if (!household?.token) {
      return;
    }
    if (isInsideAppleAppStoreApp()) {
      void onSubscribeApple(household.token, AppleProductId.Monthly, navigate, onboardingPage);
    } else {
      void startStripeCheckout(household.token, StripeSubscriptionLevel.Monthly);
    }
  }, [household, navigate, onboardingPage]);

  useEffect(() => {
    void (async () => {
      // a promotional campaign's visitor stays to read the offer, or to read why it does not apply
      if (promo) {
        return;
      }
      if (!household) {
        // TODO(hewitt): redirect back to subscribe page at end of household creation
        await goToNextOnboardingStep(navigate, { from: onboardingPage, action: 'noHousehold' });
      } else if (household.status === HouseholdStatus.Subscribed) {
        await goToNextOnboardingStep(navigate, { from: onboardingPage, action: 'alreadySubscribed' });
      }
    })();
  }, [household, navigate, onboardingPage, promo]);

  const onPromoNotApplicable = useCallback(async () => {
    await goToNextOnboardingStep(navigate, { from: onboardingPage, action: 'promoNotApplicable' });
  }, [navigate, onboardingPage]);

  if (promo && household?.status === HouseholdStatus.Subscribed) {
    return (
      <Frame
        onboardingPage={onboardingPage}
        header={<SubscribePageHeader>Subscribe</SubscribePageHeader>}
        mainContent={
          <Message $widthInChars={26}>
            Your household already subscribes to Sing Your Part, so this introductory offer does not apply. Feel free to
            pass it along to another household.
          </Message>
        }
        footerContent={<Button onClick={onPromoNotApplicable}>CONTINUE</Button>}
        onEnterKeypress={onPromoNotApplicable}
      />
    );
  }

  const showFinePrint = isInsideGooglePlayApp();
  const features = [
    "All sheet music from your church's hymnal",
    'Audio for every voice part (SATB)',
    'Full hymnal library access',
    'Daily singing plans for your household',
  ];

  return (
    <Frame
      onboardingPage={onboardingPage}
      header={<SubscribePageHeader>Subscribe</SubscribePageHeader>}
      mainContent={
        <SubscribeContent>
          <Header>
            {promo ? (
              <>
                <Headline>Your first year is free.</Headline>
                <Subhead>
                  {promoCampaignCopy[promo]?.subhead ?? 'A special offer: full access for your entire household.'}
                </Subhead>
              </>
            ) : (
              <>
                <Headline>
                  Play every song.
                  <br />
                  Every part.
                </Headline>
                <Subhead>Full access for your entire household.</Subhead>
              </>
            )}
          </Header>
          <Plans>
            {promo ? (
              // claimed with the CONTINUE button below, so the card is not a button
              <PlanCard as="div" $featured $static>
                <PlanHeader>
                  <PlanName>Annual</PlanName>
                </PlanHeader>
                <Price>First year free</Price>
                <PlanNote>No card needed. After the year, keep going for $49.99/year.</PlanNote>
              </PlanCard>
            ) : (
              <>
                <PlanCard $featured onClick={onAnnually}>
                  <BestValue>Best value</BestValue>
                  <PlanHeader>
                    <PlanName>Annual</PlanName>
                    <Savings>Save 17%</Savings>
                  </PlanHeader>
                  <Price>$49.99/year</Price>
                  <PlanNote>$4.17/mo</PlanNote>
                </PlanCard>
                <PlanCard onClick={onMonthly}>
                  <PlanHeader>
                    <PlanName>Monthly</PlanName>
                  </PlanHeader>
                  <Price>$4.99/month</Price>
                  <PlanNote>Cancel anytime</PlanNote>
                </PlanCard>
              </>
            )}
          </Plans>
          <FeatureList>
            {features.map((feature) => (
              <Feature key={feature}>
                <CheckIcon />
                <span>{feature}</span>
              </Feature>
            ))}
          </FeatureList>
          {showFinePrint && (
            <FinePrint>
              No commitment. Cancel anytime by reaching us at support@crescendosw.com at least one day before renewal
              date. Plan automatically renews until canceled.
            </FinePrint>
          )}
          <FooterNote>
            {promo
              ? 'Nothing to pay and no card to enter. Your free year covers your entire household.'
              : 'Subscription covers your entire household. Cancel anytime.'}
          </FooterNote>
        </SubscribeContent>
      }
      footerContent={promo ? <Button onClick={onAnnually}>CONTINUE</Button> : undefined}
      onEnterKeypress={onAnnually}
      alignTop={true}
    />
  );
};

const SubscribeContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.85em;
  width: min(88%, 390px);
  margin: 2.5em auto 1.5em;
  color: var(--color-text);
`;

const SubscribePageHeader = styled.div`
  height: 66px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 64px;
  font-family: Jost-SemiBold, sans-serif;
  font-size: 1.05em;
  color: var(--color-text);
`;

const Header = styled.div`
  text-align: center;
  padding-bottom: 0.1em;
`;

const Headline = styled.h1`
  margin: 0;
  font-family: Jost-SemiBold, sans-serif;
  font-size: 1.75em;
  line-height: 1.08;
  color: var(--color-text);
`;

const Subhead = styled.div`
  margin-top: 0.45em;
  font-size: 0.95em;
  color: var(--color-text-light);
`;

const Plans = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.65em;
`;

const PlanCard = styled.button<{ $featured?: boolean; $static?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.3em;
  width: 100%;
  min-height: 6em;
  padding: 0.85em 1em;
  border: ${(props) =>
    props.$featured ? '3px solid var(--color-primary-button)' : '1.5px solid var(--color-text-lighter)'};
  border-radius: 8px;
  color: var(--color-text);
  background: var(--color-background);
  text-align: left;
  cursor: ${(props) => (props.$static ? 'default' : 'pointer')};
  font-family: inherit;

  &:active {
    transform: ${(props) => (props.$static ? 'none' : 'translateY(1px)')};
  }
`;

const PlanHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75em;
`;

const PlanName = styled.div`
  font-family: Jost-SemiBold, sans-serif;
  font-size: 1em;
`;

const Savings = styled.div`
  font-family: Jost-SemiBold, sans-serif;
  font-size: 0.72em;
  color: var(--color-primary-button);
`;

const Price = styled.div`
  font-family: Jost-SemiBold, sans-serif;
  font-size: 1.45em;
  line-height: 1.1;
`;

const PlanNote = styled.div`
  font-size: 0.82em;
  color: var(--color-text-light);
`;

const BestValue = styled.div`
  position: absolute;
  top: -0.75em;
  right: 0.8em;
  padding: 0.22em 0.6em;
  border-radius: 999px;
  font-family: Jost-SemiBold, sans-serif;
  font-size: 0.62em;
  line-height: 1;
  color: var(--color-text-button-onboarding-primary);
  background: var(--color-primary-button);
`;

const FeatureList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 0.5em;
  margin: 0.25em 0 0;
  padding: 0;
  list-style: none;
`;

const Feature = styled.li`
  display: grid;
  grid-template-columns: 1.1em 1fr;
  gap: 0.55em;
  align-items: start;
  font-size: 0.78em;
  line-height: 1.25;
`;

const CheckIcon = styled.span`
  width: 0.75em;
  height: 0.45em;
  margin-top: 0.18em;
  border-left: 2px solid var(--color-primary-button);
  border-bottom: 2px solid var(--color-primary-button);
  transform: rotate(-45deg);
`;

const FooterNote = styled.div`
  padding-top: 0.25em;
  text-align: center;
  font-size: 0.74em;
  line-height: 1.3;
  color: var(--color-text-light);
`;

const FinePrint = styled.div`
  font-size: 0.62em;
  line-height: 1.25;
  color: var(--color-text-light);
`;

// iOS app 4.17+ no longer pushes subscription info via globalThis.receiveSubscriptionInfo
// (that path is broken under iOS 18 with limitsNavigationsToAppBoundDomains = true). After
// kicking off the purchase, we poll requestSubscriptionInfo until the new subscription
// appears or until the poll times out. On older iOS apps the persistent
// receiveSubscriptionInfo listener (registered in the SubscribePayment effect) handles
// the post-purchase push; the polling here ends up returning the same info (idempotent).
// The server creates the Stripe Checkout Session (stamping the householdToken into the
// subscription metadata for webhook linking - GitHub issue #3096); we just navigate to it.

async function onSubscribeApple(
  householdToken: string,
  productId: AppleProductId,
  navigate: NavigateFunction,
  from: SubscribePage,
) {
  window.webkit?.messageHandlers?.subscribe?.postMessage({ productId, householdToken });
  const appleSubscriptionInfo = await pollForAppleSubscriptionInfo();
  if (!appleSubscriptionInfo?.expirationDate) {
    return;
  }
  storeAppleSubscription(appleSubscriptionInfo);
  await goToNextOnboardingStep(navigate, { from, action: 'purchasedInApp' });
}

function storeAppleSubscription(info: AppleSubscriptionInfo) {
  localStorageSet(LocalStorageKey.ClientSubscription, {
    paymentProcessor: PaymentProcessor.Apple,
    appAccountToken: info.appAccountToken,
    subscriptionExpirationTimestamp: parseAppleDateAsTimestamp(info.expirationDate),
    productId: info.productId,
  });
}
